// Ficha 08 — Firma del contrato (FES, pista SOFOM · crédito). Look 1:1 con el mock
// `wizard/08-firma.html`: resumen del crédito (pagos, financiado, tasa y total),
// tarjeta del documento ("Ver documento" abre el PDF: vista previa antes del código y el que se
// firma después; "Ver datos" muestra el resumen),
// checkbox obligatorio de términos (autoriza el cobro automático PCF), envío del código y captura
// del OTP.
//
// INTEGRADA (guía §08 + contrato real): firma FES en DOS tiempos del motor —
//   t1 `POST …/signature/send-otp`: congela términos, genera los PDFs y despacha el OTP de firma
//      (4 dígitos; la vigencia la define messages) al celular del cliente. Re-llamarlo REENVÍA un
//      código nuevo.
//   t2 `POST …/signature/sign {otpCode}`: verifica y firma → condición 2 del checklist
//      (`contractSigned`). Errores tipados: OTP_INVALID · OTP_EXPIRED · OTP_MAX_TRIES.
// El resumen sale de `GET …/contract/preview` (credit + contractData — PII transitoria, no se
// persiste). Reanudación: currentStep ≥ 09 significa contrato ya firmado.
// El IMEI del equipo va en el contrato: se fija aquí antes de mandar el código.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, Check, FileText } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { ApiError } from '@/api/errors';
import {
    useContractDocument,
    useContractPreview,
    useSignatureSendOtp,
    useSignatureSign,
    useWizardSession,
} from '../api/useWizard';
import { ContractDeviceBlock } from '../components/ContractDeviceBlock';
import { financedImeiRequired, pinnedFinancedImei } from '../deliveryPieces';
import { FINANCED_IMEI_NOT_PINNED_MESSAGE, isFinancedImeiNotPinned } from '../financedImeiErrors';
import { wizardErrorMsg } from '../api/wizardError';
import {
    INTEGRATION_ACTION_LABEL,
    INTEGRATION_STEP,
    integrationFailure,
    type IntegrationFailure,
} from '../integrationErrors';
import { LevelChip } from '../components/LevelChip';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { creditTermsSummaryOf, type CreditTermsSummary } from '../creditTermsSummary';
import type { ActivationChecklist } from '../types';
import {
    formatCountdown,
    maskedPhone,
    OTP_RESEND_SECONDS,
    otpSentMessage,
    resendWaitSeconds,
} from '../otpResend';

const HINT = 'text-[11px] leading-[1.35] text-muted-foreground';

/** Las 4 condiciones de activación (07 → 08 → 09) — mismo bloque visual que el enganche. */
const CHECKLIST_ITEMS: { key: keyof ActivationChecklist; label: string }[] = [
    { key: 'downPayment', label: 'Enganche cobrado' },
    { key: 'contractSigned', label: 'Contrato firmado' },
    { key: 'imeiEnrolled', label: 'IMEI registrado' },
    { key: 'lockVerified', label: 'Candado activo' },
];

/** Etiquetas de las variables del contrato (CONTRACT_DATA_KEYS del wizard). */
const CONTRACT_FIELDS: { key: string; label: string }[] = [
    { key: 'nombreAcreditado', label: 'Nombre del acreditado' },
    { key: 'curp', label: 'CURP' },
    { key: 'domicilio', label: 'Domicilio' },
    { key: 'lugarFirma', label: 'Lugar de firma' },
];

/** Variables que el bloque del equipo ya muestra arriba; «Ver datos» no las repite. */
const DEVICE_BLOCK_FIELDS: ReadonlySet<string> = new Set(['nombreAcreditado', 'curp']);

const mxn = (cents: number): string => formatMXN(cents / 100);

/** Pagos semanales como renglón principal, financiado y tasa abajo en chico, y el total al pie. */
function CreditTermsCard({ summary }: { summary: CreditTermsSummary }) {
    return (
        <section
            data-testid="sign-credit-summary"
            aria-label="Resumen del crédito"
            className="flex flex-col gap-2 rounded-[10px] border border-border bg-card p-4 text-[13px]"
        >
            <div>
                <div className="text-[11.5px] text-muted-foreground">
                    Pago semanal fijo, con IVA
                </div>
                <div
                    data-testid="sign-credit-payments"
                    className="text-[18px] font-bold tabular-nums"
                >
                    {summary.weeks} pagos de {mxn(summary.weeklyCents)}
                </div>
                {summary.lastCents !== null && (
                    <div className="text-[11.5px] text-muted-foreground tabular-nums">
                        La última es de {mxn(summary.lastCents)} por el redondeo de centavos.
                    </div>
                )}
            </div>
            <div className="flex justify-between gap-4 text-[12px] text-muted-foreground">
                <span>Monto financiado</span>
                <span className="tabular-nums">{mxn(summary.financedCents)}</span>
            </div>
            <div className="flex justify-between gap-4 text-[12px] text-muted-foreground">
                <span>Tasa anual</span>
                <span className="tabular-nums">{summary.tnaPct}%</span>
            </div>
            <div className="flex items-baseline justify-between gap-4 border-t border-border pt-2">
                <span className="font-semibold">Total del crédito</span>
                <span
                    data-testid="sign-credit-total"
                    className="text-[20px] font-bold tabular-nums"
                >
                    {mxn(summary.totalCents)}
                </span>
            </div>
        </section>
    );
}

export function SignStep({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const { data: session, refresh } = useWizardSession(sessionId);
    const sendOtp = useSignatureSendOtp(sessionId);
    const sign = useSignatureSign(sessionId);
    const contractDocument = useContractDocument(sessionId);

    const [accepted, setAccepted] = useState(false);
    const [showDoc, setShowDoc] = useState(false);
    /** Destino enmascarado (`****1234`) — presente = el código ya se despachó. */
    const [masked, setMasked] = useState<string | null>(null);
    const [code, setCode] = useState('');
    const [checklist, setChecklist] = useState<ActivationChecklist | null>(null);
    const [editingImei, setEditingImei] = useState(false);
    // Segundos para poder reenviar; el reloj corre solo mientras falten.
    const [resendIn, setResendIn] = useState(0);
    // Evita un segundo envío por doble clic antes de que el botón se deshabilite.
    const sending = useRef(false);

    useEffect(() => {
        if (resendIn <= 0) return;
        const timer = setTimeout(() => {
            setResendIn((n) => n - 1);
        }, 1000);
        return () => {
            clearTimeout(timer);
        };
    }, [resendIn]);

    const creditId = session?.loan?.creditId;
    const preview = useContractPreview(sessionId, !!session && !!creditId);
    const credit = preview.data?.credit;
    // Partial: el wire solo manda las llaves que existen (curp/domicilio son opcionales).
    const contractData: Partial<Record<string, string>> = preview.data?.contractData ?? {};

    if (!session) return <Loading size="lg" />;
    const level = session.loan?.level;

    // Reanudación: `currentStep` guarda el último paso COMPLETADO (semántica advanceStep del silo,
    // verificada en DDB: tras firmar queda '08', no '09') — firmado si la sesión ya completó el 08.
    const signed = checklist !== null || parseInt(session.currentStep, 10) >= 8;
    const imeiRequired = financedImeiRequired(session);
    // Sin IMEI guardado no hay contrato que leer ni firmar; lo ya firmado se queda como está.
    const imeiReady =
        !imeiRequired || signed || (pinnedFinancedImei(session) !== '' && !editingImei);

    if (!creditId) {
        return (
            <WizardCard>
                <WizardCardHead eyebrow="Crédito SureKeep" title="Firma del contrato" />
                <WizardCardBody>
                    <p className="text-[12.5px] text-muted-foreground">
                        Todavía no hay un crédito cotizado. Regresa a Cotización y confírmala.
                    </p>
                </WizardCardBody>
                <WizardFootNav
                    back={
                        <WizardBackLink
                            sessionId={sessionId}
                            step="quote"
                            label="← Volver a la cotización"
                        />
                    }
                />
            </WizardCard>
        );
    }

    /** Rechazo de Zentral: se relee la venta y el aviso lleva al paso que lo resuelve. */
    const reportIntegration = (failure: IntegrationFailure): void => {
        void refresh();
        if (failure.recovery === 'none') {
            toast.error(failure.message);
            return;
        }
        const step = INTEGRATION_STEP[failure.recovery];
        toast.error(failure.message, {
            action: {
                label: INTEGRATION_ACTION_LABEL[failure.recovery],
                onClick: () => {
                    void navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step },
                    });
                },
            },
        });
    };

    const sendCode = async (): Promise<void> => {
        if (sending.current || resendIn > 0) return;
        sending.current = true;
        try {
            const r = await sendOtp.mutateAsync();
            // Sin `otpSent` el SMS no salió: no se anuncia ni corre la espera.
            if (!r.otpSent) {
                toast.error('No salió el código. Vuelve a mandarlo.');
                return;
            }
            const destination = maskedPhone(session.phone) ?? r.otpDestinationMasked;
            setMasked(destination);
            setCode('');
            setResendIn(OTP_RESEND_SECONDS);
            toast.success(otpSentMessage(destination));
        } catch (e) {
            if (isFinancedImeiNotPinned(e)) {
                toast.error(FINANCED_IMEI_NOT_PINNED_MESSAGE);
                void refresh();
                return;
            }
            const integration = integrationFailure(e);
            if (integration) {
                reportIntegration(integration);
                return;
            }
            const wait = resendWaitSeconds(e);
            if (wait !== undefined) {
                setResendIn(wait);
                toast.error(`Espera ${formatCountdown(wait)} para reenviar el código.`);
                return;
            }
            toast.error(wizardErrorMsg(e, 'No se pudo enviar el código — intenta de nuevo.'));
        } finally {
            sending.current = false;
        }
    };

    /**
     * Abre el PDF del contrato. La pestaña se abre en el clic: generar la vista previa tarda unos
     * segundos y, abierta después, el navegador la bloquearía. El enlace vence en minutos: se pide
     * uno nuevo cada vez.
     */
    const openDocument = async (): Promise<void> => {
        // Sin `noopener` en las features: con esa bandera `window.open` devuelve `null` aunque haya
        // abierto, y el bloqueo del navegador dejaría de distinguirse del caso bueno.
        const tab = window.open('', '_blank');
        if (!tab) {
            toast.error('Tu navegador bloqueó la ventana', {
                description: 'Permite las ventanas emergentes de este sitio e intenta de nuevo.',
            });
            return;
        }
        tab.opener = null;
        tab.document.title = 'Contrato de Crédito SureKeep';
        tab.document.body.textContent = 'Generando el contrato…';
        try {
            const link = await contractDocument.mutateAsync();
            tab.location.href = link.url;
        } catch (e) {
            tab.close();
            if (e instanceof ApiError && e.code === 'CONTRACT_DETAILS_INCOMPLETE') {
                toast.error('Faltan datos del cliente o del equipo para armar el contrato.');
                return;
            }
            toast.error(wizardErrorMsg(e, 'No se pudo abrir el contrato — intenta de nuevo.'));
        }
    };

    const signContract = async (): Promise<void> => {
        try {
            const r = await sign.mutateAsync(code);
            setChecklist(r.checklist);
            toast.success('Contrato firmado.');
        } catch (e) {
            const integration = integrationFailure(e);
            if (integration) {
                reportIntegration(integration);
                return;
            }
            if (e instanceof ApiError) {
                if (e.code === 'OTP_INVALID') {
                    toast.error('Código incorrecto — revísalo e intenta de nuevo.');
                    return;
                }
                if (e.code === 'OTP_EXPIRED') {
                    toast.error('El código venció — reenvíalo.');
                    return;
                }
                if (e.code === 'OTP_MAX_TRIES') {
                    toast.error('Se agotaron los intentos — reenvía un código nuevo.');
                    setCode('');
                    return;
                }
            }
            toast.error(wizardErrorMsg(e, 'No se pudo firmar el contrato — intenta de nuevo.'));
        }
    };

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow="Crédito SureKeep"
                title="Firma del contrato"
                right={<LevelChip level={level} />}
                sub="El cliente revisa el contrato y firma con un código que le llega al celular."
            />

            <WizardCardBody className="flex flex-col gap-4">
                {preview.isPending ? (
                    <Loading size="lg" />
                ) : preview.isError ? (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <p className="text-[12.5px] text-muted-foreground">
                            {wizardErrorMsg(
                                preview.error,
                                'No se pudo leer el resumen del contrato.',
                            )}
                        </p>
                        <Button
                            size="sm"
                            isLoading={preview.isFetching}
                            onClick={() => void preview.refetch()}
                        >
                            Reintentar
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* ── Resumen del crédito: solo los valores del crédito ── */}
                        {credit && <CreditTermsCard summary={creditTermsSummaryOf(credit)} />}

                        {/* ── Documento del contrato ── */}
                        <div className="rounded-[10px] border border-border bg-card p-4">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-3">
                                    <span className="flex size-10 flex-none items-center justify-center rounded-[10px] bg-info-soft text-info">
                                        <FileText className="size-5" aria-hidden />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-[13px] font-semibold">
                                            Contrato de Crédito SureKeep
                                        </div>
                                        <div className={HINT}>
                                            Autoriza el cobro semanal automático
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-none gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={!imeiReady}
                                        onClick={() => {
                                            setShowDoc((v) => !v);
                                        }}
                                    >
                                        {showDoc ? 'Ocultar' : 'Ver datos'}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={!imeiReady}
                                        isLoading={contractDocument.isPending}
                                        onClick={() => void openDocument()}
                                    >
                                        Ver documento
                                    </Button>
                                </div>
                            </div>
                            {imeiRequired && (
                                <ContractDeviceBlock
                                    sessionId={sessionId}
                                    session={session}
                                    contractData={contractData}
                                    locked={signed || masked !== null}
                                    editing={editingImei}
                                    onEditingChange={(editing) => {
                                        setEditingImei(editing);
                                        // Otro IMEI cambia el contrato: se vuelve a aceptar.
                                        if (editing) setAccepted(false);
                                    }}
                                    onPinned={() => void preview.refetch()}
                                    onReload={() => void refresh()}
                                />
                            )}
                            {showDoc && imeiReady && (
                                <div className="mt-3 border-t border-border pt-3">
                                    {CONTRACT_FIELDS.filter(
                                        (f) =>
                                            contractData[f.key] !== undefined &&
                                            !(imeiRequired && DEVICE_BLOCK_FIELDS.has(f.key)),
                                    ).map((f) => (
                                        <div
                                            key={f.key}
                                            className="flex items-start justify-between gap-4 py-1 text-[12.5px]"
                                        >
                                            <span className="flex-none text-muted-foreground">
                                                {f.label}
                                            </span>
                                            <span className="text-right font-medium">
                                                {contractData[f.key]}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {signed ? (
                            /* ── Estado firmado (o reanudación) ── */
                            <div className="rounded-lg border border-border bg-card p-4">
                                <div className="flex items-center gap-2">
                                    <span className="flex size-6 flex-none items-center justify-center rounded-full bg-success/15 text-success">
                                        <Check className="size-3.5" strokeWidth={3} aria-hidden />
                                    </span>
                                    <p className="text-[13px] font-semibold">Contrato firmado</p>
                                </div>
                                <p className={cn(HINT, 'mt-2')}>
                                    Falta la entrega para activar el crédito.
                                </p>
                                {checklist && (
                                    <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                                        {CHECKLIST_ITEMS.map((item) => {
                                            const done = checklist[item.key];
                                            return (
                                                <div
                                                    key={item.key}
                                                    className={cn(
                                                        'flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[11.5px]',
                                                        done
                                                            ? 'bg-success/10 font-medium text-success'
                                                            : 'bg-muted/50 text-muted-foreground',
                                                    )}
                                                >
                                                    <Check
                                                        className={cn(
                                                            'size-3',
                                                            !done && 'opacity-30',
                                                        )}
                                                        strokeWidth={3}
                                                        aria-hidden
                                                    />
                                                    {item.label}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                {/* ── Aceptación de términos (obligatoria para enviar el código) ── */}
                                <div
                                    className={cn(
                                        'rounded-lg border border-border bg-card p-4',
                                        !imeiReady && 'opacity-50',
                                    )}
                                >
                                    <div className="flex items-start gap-2">
                                        <input
                                            id="sign-accepted"
                                            type="checkbox"
                                            disabled={!imeiReady}
                                            checked={accepted && imeiReady}
                                            onChange={(e) => {
                                                setAccepted(e.target.checked);
                                            }}
                                            className="mt-0.5 size-4 flex-none accent-primary"
                                        />
                                        <label
                                            htmlFor="sign-accepted"
                                            className="min-w-0 flex-1 cursor-pointer"
                                        >
                                            <span className="block text-[13px] font-semibold">
                                                Acepto los términos del contrato de crédito
                                            </span>
                                            <span className={cn(HINT, 'mt-0.5 block')}>
                                                Autoriza el cobro automático semanal desde su cuenta
                                                PagoConFiado durante todo el plazo del crédito.
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {/* ── Código para firmar ── */}
                                <div
                                    className={cn(
                                        'rounded-lg border border-border bg-muted/30 p-4',
                                        !imeiReady && 'opacity-50',
                                    )}
                                >
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div className="min-w-[220px] flex-1">
                                            <div className="text-[13px] font-semibold">
                                                Código para firmar
                                            </div>
                                            <div className={cn(HINT, 'mt-0.5')}>
                                                {masked
                                                    ? `${otpSentMessage(masked)} Si lo reenvías, usa el último que llegue.`
                                                    : imeiReady
                                                      ? 'Marca que acepta el contrato para poder mandar el código.'
                                                      : 'Primero guarda el IMEI del equipo.'}
                                            </div>
                                        </div>
                                        <Button
                                            variant={masked ? 'outline' : 'default'}
                                            size="sm"
                                            disabled={!accepted || !imeiReady || resendIn > 0}
                                            isLoading={sendOtp.isPending}
                                            onClick={() => void sendCode()}
                                        >
                                            {resendIn > 0
                                                ? `Reenviar en ${formatCountdown(resendIn)}`
                                                : masked
                                                  ? 'Reenviar código'
                                                  : 'Enviar código'}
                                        </Button>
                                    </div>

                                    {masked && (
                                        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                                            <input
                                                inputMode="numeric"
                                                autoComplete="one-time-code"
                                                maxLength={4}
                                                placeholder="0000"
                                                aria-label="Código de firma (4 dígitos)"
                                                value={code}
                                                onChange={(e) => {
                                                    setCode(
                                                        e.target.value
                                                            .replace(/\D/g, '')
                                                            .slice(0, 4),
                                                    );
                                                }}
                                                className="h-10 w-[120px] rounded-md border border-input bg-background text-center font-mono text-[18px] font-bold tracking-[0.3em] tabular-nums outline-none focus:border-ring"
                                            />
                                            <Button
                                                disabled={code.length !== 4}
                                                isLoading={sign.isPending}
                                                onClick={() => void signContract()}
                                            >
                                                Firmar contrato
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </WizardCardBody>

            {/* Mismo caso que el 07 de crédito: el «Guardar y salir» de aquí era un toast que no
                guardaba nada. Pasa al pie compartido, que abre el modal del motivo. */}
            <WizardFootNav back={<WizardBackLink sessionId={sessionId} step="payment" />}>
                <Button
                    disabled={!signed}
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() =>
                        void navigate({
                            to: '/wizard/s/$sessionId/$step',
                            params: { sessionId, step: 'delivery' },
                        })
                    }
                >
                    Continuar a Entrega
                </Button>
            </WizardFootNav>
        </WizardCard>
    );
}
